import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  AsyncValidatorFn,
} from '@angular/forms';
import { VendorService } from '@shared/services/vendor.service';
import { Observable, of } from 'rxjs';
import { ACCESS_CONTROL_TEXT } from '../access-control-text.constants';

@Component({
  selector: 'app-add-vendor-poc',
  templateUrl: './add-vendor-poc.component.html',
  styleUrls: ['./add-vendor-poc.component.scss'],
})
export class AddVendorPocComponent implements OnInit, OnChanges {
  ACCESS_CONTROL_TEXT = ACCESS_CONTROL_TEXT;
  vendorPocForm!: FormGroup;
  vendorOptions: { label: string; value: string | number }[] = [];
  @Output() cancel = new EventEmitter<void>();
  @Output() submit = new EventEmitter<any>();
  @Input() existingEmails: string[] = [];
  @Input() vendorData: any[] = [];
  constructor(
    private fb: FormBuilder,
    private vendorService: VendorService,
  ) {
    this.createForm();
  }

  createForm() {
    this.vendorPocForm = this.fb.group({
      vendorName: ['', Validators.required],
      vendorRep: ['', Validators.required],
      email: [
        '',
        [Validators.required, Validators.email],
        [this.uniqueEmailValidator(this.existingEmails)],
      ],
      contact: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      iskillAccess: [false],
      vendorAccess: [false],
    });
  }

  ngOnInit(): void {
    this.vendorService.fetchVendorDetails().subscribe({
      next: (res) => {
        const vendors = res?.data?.vendors || [];
        this.vendorOptions = vendors.map((v: any) => ({
          label: v.name,
          value: v.id,
        }));
      },
      error: () => {
        this.vendorOptions = [];
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vendorData'] && this.vendorData) {
      const emails = (this.vendorData || [])
        .map((v) => v.email?.toLowerCase())
        .filter((e: string) => !!e);
      this.setExistingEmails(emails);
    }
  }

  uniqueEmailValidator(existingEmails: string[]): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const email = control.value;
      if (!email) return of(null);
      const exists = existingEmails.includes(email.toLowerCase());
      return of(exists ? { emailTaken: true } : null);
    };
  }

  setExistingEmails(emails: string[]) {
    this.vendorPocForm
      .get('email')
      ?.setAsyncValidators(this.uniqueEmailValidator(emails));
    this.vendorPocForm.get('email')?.updateValueAndValidity();
  }

  onCancel(): void {
    this.resetForm();
    this.cancel.emit();
  }

  resetForm(): void {
    this.vendorPocForm.reset({
      vendorName: '',
      vendorRep: '',
      email: '',
      contact: '',
      vendorAccess: false,
      iskillAccess: false
    });
    this.vendorPocForm.markAsPristine();
    this.vendorPocForm.markAsUntouched();
    this.vendorPocForm.updateValueAndValidity();
  }

  onSubmit(): void {
    this.vendorPocForm.markAllAsTouched();
    this.vendorPocForm.updateValueAndValidity();
    if (this.vendorPocForm.valid) {
      const formValue = this.vendorPocForm.value;
      const selectedVendor = this.vendorOptions.find(
        (v) => v.label === formValue.vendorName?.label,
      );
      const isActive = Boolean(formValue.vendorAccess);
      const payload = {
        vendorId: selectedVendor?.value,
        pointOfContact: formValue.vendorRep,
        contact: formValue.contact,
        email: formValue.email,
        status: isActive ? 'active' : 'inactive',
        iskillAccess: isActive && Boolean(formValue.iskillAccess),
      };
      this.submit.emit(payload);
    }
  }
}
